import dayjs from "dayjs";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useBlocker } from "react-router-dom";
import { BreadBudget, FirestoreDocBudget } from "breadcommon";
import { CategoriesContext } from "../firebaseio/CategoriesContext";
import {
  firestoreDeleteBudget,
  firestoreCreateNewEmptyBudget,
  firestoreDuplicateBudget,
  firestoreOverwriteBudget,
} from "../firebaseio/firestoreIo";
import { UserContext } from "../firebaseio/UserContext";
import { RyeModalContent, useRyeModal } from "../rye/RyeModal";
import { RyeButton } from "../rye/RyeButton";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { RyeMainContentHeader } from "../rye/RyeMainContentHeader";
import {
  RyeMainContentScrollShadow,
  RyeScrollListener,
  useRyeScrolled,
} from "../rye/RyeMainContentScrollShadow";
import { buildClasses } from "../utils/buildClasses";
import { RyeScreenWrapper } from "../rye/RyeScreenWrapper";
import { BudgetsContext } from "../firebaseio/BudgetsContext";
import { PreferencesContext } from "../firebaseio/PreferencesContext";
import { LoadingInitialDataContext } from "../LoadUser";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import { RyeSkeleton } from "../rye/RyeSkeleton";
import { BudgetEditor } from "./BudgetEditor";
import { BudgetHeader } from "./BudgetHeader";
import {
  BudgetTrackingReport,
  BudgetTrackingViewMode,
  ColumnVisibility,
} from "./BudgetTrackingReport";
import {
  isBudgetActive,
  pickDefaultBudgetId,
  sortBudgetsByEndDesc,
} from "./budgetUtils";
import { RyeEmptyState } from "../rye/RyeEmptyState";
import { MAIN_NAV_DEFINITIONS } from "../main_nav/mainNavDefinitions";
import { useOpenSettings } from "../settings_screen/settingsDefinitions";

function createSnapshot(budget: BreadBudget): FirestoreDocBudget {
  const lineItems: { [categoryId: string]: { formula: string } } = {};
  budget.line_items.forEach((lineItem, categoryId) => {
    lineItems[categoryId] = { formula: lineItem.formula };
  });
  return {
    name: budget.name,
    begin_date_timestamp_secs: budget.beginDateTimestampSecs.unix(),
    end_date_timestamp_secs: budget.endDateTimestampSecs.unix(),
    currency: budget.currency,
    line_items: lineItems,
  };
}

/** Track compares actuals against a budget; Plan creates and edits budgets. */
type BudgetsView = "track" | "plan";

export function BudgetsScreen() {
  const [selectedBudgetId, setSelectedBudgetId] = useState<string | null>(null);
  const [newlyCreatedBudgetId, setNewlyCreatedBudgetId] = useState<
    string | null
  >(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editSnapshot, setEditSnapshot] = useState<FirestoreDocBudget | null>(
    null,
  );
  const [autoEditNewBudget, setAutoEditNewBudget] = useState<boolean>(false);
  const [showHistorical, setShowHistorical] = useState<boolean>(false);
  // Shades the top bar once whichever body is showing is scrolled.
  const { scrolled, onScroll: onBodyScroll } = useRyeScrolled();
  // Null until the user picks a view, so the default can follow the budgets
  // once they load.
  const [chosenView, setChosenView] = useState<BudgetsView | null>(null);
  const [trackingViewMode, setTrackingViewMode] =
    useState<BudgetTrackingViewMode>("graph");
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    totalBudgeted: true,
    budgetedToDate: true,
    actualsToDate: true,
    monthlyActuals: true,
  });
  const { openModal } = useRyeModal();
  const user = useContext(UserContext);
  const loadingInitialData = useContext(LoadingInitialDataContext);

  const budgets = useContext(BudgetsContext);

  const today = dayjs();
  const hasActiveBudget = Array.from(budgets.values()).some((budget) =>
    isBudgetActive(budget, today),
  );
  // Tracking is what people come back for, so it's the default whenever
  // there's a budget to track; otherwise open on planning one.
  const selectedView: BudgetsView =
    chosenView ?? (hasActiveBudget ? "track" : "plan");
  const hasBudgets = budgets.size > 0;
  // Editing happens in Plan's editor. Showing it without changing
  // selectedView means finishing an edit returns to whichever mode it began in.
  // With no budgets there's nothing to track, so Plan shows its empty state.
  const view: BudgetsView = isEditing || !hasBudgets ? "plan" : selectedView;
  const selectedBudget = selectedBudgetId
    ? (budgets.get(selectedBudgetId) ?? null)
    : null;

  const isLoading =
    loadingInitialData.budgets ||
    loadingInitialData.categories ||
    loadingInitialData.transactions;

  useEffect(() => {
    if (budgets.size === 0) {
      setSelectedBudgetId(null);
      return;
    }

    if (
      (!selectedBudgetId || !budgets.has(selectedBudgetId)) &&
      budgets.size > 0
    ) {
      setSelectedBudgetId(pickDefaultBudgetId(budgets));
    }
  }, [selectedBudgetId, budgets, budgets.size]);

  // Clear the newly created budget ID after focusing
  useEffect(() => {
    if (newlyCreatedBudgetId && selectedBudgetId === newlyCreatedBudgetId) {
      const timer = setTimeout(() => {
        setNewlyCreatedBudgetId(null);
      }, 100); // Small delay to ensure the component has rendered
      return () => clearTimeout(timer);
    }
  }, [newlyCreatedBudgetId, selectedBudgetId]);

  // Auto-enter edit mode for newly created/duplicated budgets
  useEffect(() => {
    if (
      autoEditNewBudget &&
      newlyCreatedBudgetId &&
      budgets.has(newlyCreatedBudgetId)
    ) {
      const budget = budgets.get(newlyCreatedBudgetId)!;
      setEditSnapshot(createSnapshot(budget));
      setIsEditing(true);
      setAutoEditNewBudget(false);
    }
  }, [autoEditNewBudget, newlyCreatedBudgetId, budgets]);

  // Browser tab close guard
  useEffect(() => {
    if (!isEditing) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isEditing]);

  function enterEditMode() {
    if (!selectedBudgetId) return;
    const budget = budgets.get(selectedBudgetId);
    if (!budget) return;
    setEditSnapshot(createSnapshot(budget));
    setIsEditing(true);
    pinSelectedView();
  }

  // Called when an edit starts. An edit can change which mode is the default
  // (e.g. creating the first active budget), so fix the current one in place.
  // Closures capture the mode at click time, before an awaited create resolves.
  function pinSelectedView() {
    setChosenView(selectedView);
  }

  function exitEditMode() {
    setIsEditing(false);
    setEditSnapshot(null);
  }

  async function discardChanges() {
    if (!selectedBudgetId || !editSnapshot || !user) return;
    await firestoreOverwriteBudget(user.uid, selectedBudgetId, editSnapshot);
    setIsEditing(false);
    setEditSnapshot(null);
  }

  const openUnsavedChangesModal = useCallback(
    (onProceed: (kept: boolean) => void, onCancel: () => void) => {
      openModal(
        <RyeModalContent
          title="Unsaved editing session"
          content="Would you like to keep your changes or discard them?"
          actionButtons={[
            {
              text: "Cancel",
              vibe: "subdued",
              action: () => {
                onCancel();
              },
            },
            {
              text: "Discard changes",
              vibe: "danger",
              action: async () => {
                if (selectedBudgetId && editSnapshot && user) {
                  await firestoreOverwriteBudget(
                    user.uid,
                    selectedBudgetId,
                    editSnapshot,
                  );
                }
                setIsEditing(false);
                setEditSnapshot(null);
                onProceed(false);
              },
            },
            {
              text: "Keep changes",
              vibe: "primary",
              variant: "filled",
              action: () => {
                setIsEditing(false);
                setEditSnapshot(null);
                onProceed(true);
              },
            },
          ]}
        />,
      );
    },
    [openModal, selectedBudgetId, editSnapshot, user],
  );

  // React Router navigation guard
  const blocker = useBlocker(isEditing);
  const blockerHandledRef = useRef(false);

  useEffect(() => {
    if (blocker.state === "blocked" && !blockerHandledRef.current) {
      blockerHandledRef.current = true;
      openUnsavedChangesModal(
        () => {
          blockerHandledRef.current = false;
          blocker.proceed();
        },
        () => {
          blockerHandledRef.current = false;
          blocker.reset();
        },
      );
    }
  }, [blocker, openUnsavedChangesModal]);

  function handleBudgetSwitch(newBudgetId: string) {
    if (isEditing) {
      openUnsavedChangesModal(
        () => {
          setSelectedBudgetId(newBudgetId);
        },
        () => {},
      );
    } else {
      setSelectedBudgetId(newBudgetId);
    }
  }

  async function duplicateSelectedBudget() {
    if (!selectedBudgetId || !user) return;
    const budget = budgets.get(selectedBudgetId);
    if (!budget) return;
    try {
      const duplicatedBudgetId = await firestoreDuplicateBudget(
        user.uid,
        selectedBudgetId,
        `Copy of ${budget.name}`,
      );
      setNewlyCreatedBudgetId(duplicatedBudgetId);
      setSelectedBudgetId(duplicatedBudgetId);
      setAutoEditNewBudget(true);
      pinSelectedView();
    } catch (error) {
      console.error("Failed to duplicate budget:", error);
    }
  }

  function deleteSelectedBudget() {
    if (!selectedBudgetId || !user) return;
    const budget = budgets.get(selectedBudgetId);
    if (!budget) return;
    openModal(
      <RyeModalContent
        title={`Delete "${budget.name}"?`}
        content="Are you sure you want to delete this budget? This action cannot be undone."
        actionButtons={[
          { text: "Cancel", vibe: "subdued", action: () => {} },
          {
            text: "Delete",
            vibe: "danger",
            variant: "filled",
            action: () => {
              firestoreDeleteBudget(user.uid, selectedBudgetId);
              setIsEditing(false);
              setEditSnapshot(null);
            },
          },
        ]}
      />,
    );
  }

  function onBudgetCreated(budgetId: string) {
    setNewlyCreatedBudgetId(budgetId);
    setSelectedBudgetId(budgetId);
    setAutoEditNewBudget(true);
    pinSelectedView();
  }

  // The selected budget's name, actions, and months, atop both views.
  const header = selectedBudget && (
    <BudgetHeader
      budget={selectedBudget}
      budgets={sortBudgetsByEndDesc(budgets)}
      onSelectBudget={handleBudgetSwitch}
      isEditing={isEditing}
      focusNameInput={selectedBudgetId === newlyCreatedBudgetId}
      onEdit={enterEditMode}
      onDuplicate={duplicateSelectedBudget}
      onDelete={deleteSelectedBudget}
    />
  );

  return (
    <RyeScreenWrapper>
      <RyeMainContentHeader title="Budgets">
        {/* Only needs the budgets themselves. */}
        {!loadingInitialData.budgets && hasBudgets && (
          <div className={buildClasses("w-full", "flex", "items-center")}>
            {/* Centred on the whole bar, not just the space after the title:
            the view switch, or while editing, saving and discarding, which
            stay in reach however far the budget scrolls. */}
            <div
              className={buildClasses(
                "absolute",
                "left-1/2",
                "-translate-x-1/2",
                "flex",
                "items-center",
                "gap-3",
              )}
            >
              {isEditing ? (
                <>
                  <RyeButton
                    text="Save"
                    icon="check"
                    size="sm"
                    vibe="primary"
                    variant="filled"
                    onClick={exitEditMode}
                  />
                  <RyeButton
                    text="Discard changes"
                    icon="undo"
                    size="sm"
                    vibe="subdued"
                    onClick={discardChanges}
                  />
                </>
              ) : (
                <RyeSegmentedButton
                  entries={[
                    {
                      id: "track",
                      displayText: "Track against budget",
                      icon: "monitoring",
                      onClick: () => setChosenView("track"),
                    },
                    {
                      id: "plan",
                      displayText: "View budget only",
                      icon: "view_list",
                      onClick: () => setChosenView("plan"),
                    },
                  ]}
                  initEntryId={view}
                  selectedEntryId={view}
                />
              )}
            </div>
            <div
              className={buildClasses(
                "flex",
                "items-center",
                "gap-3",
                "ml-auto",
              )}
            >
              {!isEditing && (
                <CreateNewBudgetButton onBudgetCreated={onBudgetCreated} />
              )}
              {/* Points the way the historical panel slides, at the right
              edge it opens from. */}
              {view === "plan" && selectedBudget && (
                <RyeButton
                  text={
                    showHistorical
                      ? "Hide historical data"
                      : "Show historical data"
                  }
                  icon={showHistorical ? "chevron_right" : "chevron_left"}
                  size="sm"
                  vibe="subdued"
                  onClick={() => setShowHistorical(!showHistorical)}
                />
              )}
            </div>
          </div>
        )}
      </RyeMainContentHeader>
      <RyeMainContentScrollShadow visible={scrolled} />
      {isLoading ? (
        <RyeMainContentCard roundedTl={false} roundedTr={false}>
          {!loadingInitialData.budgets && header}
          <BudgetsBodySkeleton />
        </RyeMainContentCard>
      ) : view === "track" && selectedBudget ? (
        <BudgetTrackingReport
          key={selectedBudget.id}
          budget={selectedBudget}
          header={header}
          viewMode={trackingViewMode}
          onViewModeChange={setTrackingViewMode}
          columnVisibility={columnVisibility}
          setColumnVisibility={setColumnVisibility}
          onScroll={onBodyScroll}
        />
      ) : view === "track" ? (
        // Only until the selection effect picks a budget.
        <RyeMainContentCard roundedTl={false} roundedTr={false}>
          {null}
        </RyeMainContentCard>
      ) : !hasBudgets ? (
        <RyeMainContentCard roundedTl={false} roundedTr={false}>
          <NoBudgetsYet onBudgetCreated={onBudgetCreated} />
        </RyeMainContentCard>
      ) : (
        <RyeMainContentCard roundedTl={false} roundedTr={false}>
          <RyeScrollListener onScroll={onBodyScroll} />
          {header}
          <BudgetEditor
            budget={selectedBudget}
            isEditing={isEditing}
            showHistorical={showHistorical}
          />
        </RyeMainContentCard>
      )}
    </RyeScreenWrapper>
  );
}

function CreateNewBudgetButton({
  onBudgetCreated,
  appearance = "header",
}: {
  onBudgetCreated: (budgetId: string) => void;
  /** "header" is the top bar's button; the others are full-size calls to action. */
  appearance?: "header" | "primary" | "secondary";
}): JSX.Element {
  const isHeader = appearance === "header";
  const user = useContext(UserContext);
  const categories = useContext(CategoriesContext);
  const userPrefs = useContext(PreferencesContext);

  return (
    <div>
      <RyeButton
        text={isHeader ? "New budget" : "Create a budget"}
        icon="add"
        onClick={async () => {
          if (!user) {
            return;
          }
          const newBudgetId = await firestoreCreateNewEmptyBudget(
            user.uid,
            `New Budget`,
            dayjs().startOf("year"),
            dayjs().endOf("year"),
            Array.from(categories.keys()),
            userPrefs.web.default_currency ?? "USD",
          );
          onBudgetCreated(newBudgetId);
        }}
        size={isHeader ? "sm" : "md"}
        vibe={appearance === "primary" ? "primary" : "subdued"}
        variant={appearance === "primary" ? "filled" : "outlined"}
        shadow={isHeader ? "sm" : undefined}
      />
    </div>
  );
}

/** Shown in place of the editor until the first budget exists. */
function NoBudgetsYet({
  onBudgetCreated,
}: {
  onBudgetCreated: (budgetId: string) => void;
}): JSX.Element {
  const categories = useContext(CategoriesContext);
  const openSettings = useOpenSettings();
  const hasCategories = categories.size > 0;

  return (
    <RyeEmptyState
      icon={MAIN_NAV_DEFINITIONS.BUDGETS.icon}
      title="Plan ahead with a budget"
      description="Estimate how much you expect to earn, spend, and save across your categories. Then, track your actual activity against your estimates so you can spend with confidence and meet your goals."
      note={
        hasCategories
          ? undefined
          : "Budgets are set per category, so you’ll want some categories first."
      }
      actions={
        <>
          {!hasCategories && (
            <RyeButton
              text="Create categories"
              icon="category"
              vibe="primary"
              variant="filled"
              onClick={() => openSettings("categories")}
            />
          )}
          <CreateNewBudgetButton
            appearance={hasCategories ? "primary" : "secondary"}
            onBudgetCreated={onBudgetCreated}
          />
        </>
      }
    />
  );
}

/** Stands in for the chart and category table while their data loads. */
function BudgetsBodySkeleton(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading budget"
      className={buildClasses("flex", "flex-col", "gap-6", "pt-4")}
    >
      <RyeSkeleton className={buildClasses("h-64", "w-full", "rounded-lg")} />
      <div className={buildClasses("flex", "flex-col", "gap-3")}>
        {Array.from({ length: 6 }, (_, i) => (
          <div
            key={i}
            className={buildClasses("flex", "items-center", "gap-4")}
          >
            <RyeSkeleton className={buildClasses("h-4", "w-40")} />
            <RyeSkeleton className={buildClasses("h-4", "w-20", "ml-auto")} />
            <RyeSkeleton className={buildClasses("h-4", "w-20")} />
          </div>
        ))}
      </div>
    </div>
  );
}
